import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PasscodeInput from '@/components/PasscodeInput';
import PhoneInput from '@/components/PhoneInput';
import { User, Mail, Phone, Building2, Printer, IdCard, Save, Clock, Lock, Key, AlertCircle, Zap } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { cleanPhone, formatPhoneDisplay } from '@/utils/phoneUtils';
import { US_STATES } from '@/components/StateSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// All US timezones
const US_TIMEZONES = [
  // Eastern Time Zone
  { value: 'America/New_York', label: 'Eastern Time - New York (ET)' },
  { value: 'America/Detroit', label: 'Eastern Time - Detroit (ET)' },
  { value: 'America/Kentucky/Louisville', label: 'Eastern Time - Louisville (ET)' },
  { value: 'America/Kentucky/Monticello', label: 'Eastern Time - Monticello (ET)' },
  { value: 'America/Indiana/Indianapolis', label: 'Eastern Time - Indianapolis (ET)' },
  { value: 'America/Indiana/Vincennes', label: 'Eastern Time - Vincennes (ET)' },
  { value: 'America/Indiana/Winamac', label: 'Eastern Time - Winamac (ET)' },
  { value: 'America/Indiana/Marengo', label: 'Eastern Time - Marengo (ET)' },
  { value: 'America/Indiana/Petersburg', label: 'Eastern Time - Petersburg (ET)' },
  { value: 'America/Indiana/Vevay', label: 'Eastern Time - Vevay (ET)' },
  
  // Central Time Zone
  { value: 'America/Chicago', label: 'Central Time - Chicago (CT)' },
  { value: 'America/Indiana/Tell_City', label: 'Central Time - Tell City (CT)' },
  { value: 'America/Indiana/Knox', label: 'Central Time - Knox (CT)' },
  { value: 'America/Menominee', label: 'Central Time - Menominee (CT)' },
  { value: 'America/North_Dakota/Center', label: 'Central Time - Center, ND (CT)' },
  { value: 'America/North_Dakota/New_Salem', label: 'Central Time - New Salem, ND (CT)' },
  { value: 'America/North_Dakota/Beulah', label: 'Central Time - Beulah, ND (CT)' },
  
  // Mountain Time Zone
  { value: 'America/Denver', label: 'Mountain Time - Denver (MT)' },
  { value: 'America/Boise', label: 'Mountain Time - Boise (MT)' },
  { value: 'America/Phoenix', label: 'Mountain Time - Phoenix (MST, no DST)' },
  
  // Pacific Time Zone
  { value: 'America/Los_Angeles', label: 'Pacific Time - Los Angeles (PT)' },
  
  // Alaska Time Zone
  { value: 'America/Anchorage', label: 'Alaska Time - Anchorage (AKT)' },
  { value: 'America/Juneau', label: 'Alaska Time - Juneau (AKT)' },
  { value: 'America/Sitka', label: 'Alaska Time - Sitka (AKT)' },
  { value: 'America/Metlakatla', label: 'Alaska Time - Metlakatla (AKT)' },
  { value: 'America/Yakutat', label: 'Alaska Time - Yakutat (AKT)' },
  { value: 'America/Nome', label: 'Alaska Time - Nome (AKT)' },
  { value: 'America/Adak', label: 'Hawaii-Aleutian Time - Adak (HAT)' },
  
  // Hawaii Time Zone
  { value: 'Pacific/Honolulu', label: 'Hawaii Time - Honolulu (HT, no DST)' },
  
  // US Territories
  { value: 'America/Puerto_Rico', label: 'Atlantic Time - Puerto Rico (AST, no DST)' },
  { value: 'America/Virgin', label: 'Atlantic Time - US Virgin Islands (AST, no DST)' },
  { value: 'Pacific/Guam', label: 'Chamorro Time - Guam (ChST)' },
  { value: 'Pacific/Saipan', label: 'Chamorro Time - Saipan (ChST)' },
  { value: 'Pacific/Pago_Pago', label: 'Samoa Time - American Samoa (SST)' },
  { value: 'Pacific/Wake', label: 'Wake Island Time (WAKT)' },
];

const Settings = () => {
  const { agent, token, setAgent } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: agent?.name || '',
    phone: agent?.phone || '',
    city: agent?.city || '',
    state: agent?.state || 'NJ',
    office_address: agent?.office_address || '',
    office_phone: agent?.office_phone || '',
    fax: agent?.fax || '',
    license_id: agent?.license_id || '',
    timezone: agent?.timezone || 'America/New_York',
    share_visitor_data_with_broker: false,
    mask_lead_contact_info: false,
    email_signature: agent?.email_signature || ''
  });

  // Passcode security states
  const [showPasscodeVerify, setShowPasscodeVerify] = useState(false);
  const [showPasscodeSetup, setShowPasscodeSetup] = useState(false);
  const [showPasscodeChange, setShowPasscodeChange] = useState(false);
  const [showPasscodeReset, setShowPasscodeReset] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [newPasscode, setNewPasscode] = useState('');
  const [confirmPasscode, setConfirmPasscode] = useState('');
  const [currentPasscode, setCurrentPasscode] = useState('');
  const [passcodeVerifying, setPasscodeVerifying] = useState(false);
  const [usePasswordForPasscode, setUsePasswordForPasscode] = useState(false);
  const [passcodeResetPassword, setPasscodeResetPassword] = useState('');
  
  // Password change states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const isBroker = agent?.role === 'broker';

  // Update formData when agent data loads/changes
  useEffect(() => {
    if (agent) {
      setFormData({
        name: agent.name || '',
        phone: agent.phone || '',
        city: agent.city || '',
        state: agent.state || 'NJ',
        office_address: agent.office_address || '',
        office_phone: agent.office_phone || '',
        fax: agent.fax || '',
        license_id: agent.license_id || '',
        timezone: agent.timezone || 'America/New_York',
        share_visitor_data_with_broker: false,
        mask_lead_contact_info: false,
        email_signature: agent.email_signature || ''
      });
    }
  }, [agent]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      // Clean phone numbers before submission
      const cleanedData = {
        ...formData,
        phone: cleanPhone(formData.phone),
        office_phone: cleanPhone(formData.office_phone),
        fax: cleanPhone(formData.fax)
      };

      const response = await axios.put(`${API}/profile`, cleanedData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update the agent context with new data
      setAgent(response.data);
      
      toast.success('Profile updated successfully!');
      setIsEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original values
    setFormData({
      name: agent?.name || '',
      phone: agent?.phone || '',
      city: agent?.city || '',
      state: agent?.state || 'NJ',
      office_address: agent?.office_address || '',
      office_phone: agent?.office_phone || '',
      fax: agent?.fax || '',
      license_id: agent?.license_id || '',
      timezone: agent?.timezone || 'America/New_York',
      share_visitor_data_with_broker: false,
      email_signature: agent?.email_signature || ''
    });
    setIsEditing(false);
  };

  // Passcode handlers
  const handleEditClick = () => {
    // Check if passcode is set
    if (agent?.has_passcode) {
      setShowPasscodeVerify(true);
    } else {
      // No passcode, show setup dialog
      setShowPasscodeSetup(true);
    }
  };

  const handlePasscodeSetup = async () => {
    if (newPasscode.length !== 6) {
      toast.error('Passcode must be exactly 6 digits');
      return;
    }
    if (newPasscode !== confirmPasscode) {
      toast.error('Passcodes do not match');
      return;
    }

    setPasscodeVerifying(true);
    try {
      await axios.post(
        `${API}/passcode/setup`,
        { passcode: newPasscode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Passcode set successfully!');
      setShowPasscodeSetup(false);
      setIsEditing(true);
      setNewPasscode('');
      setConfirmPasscode('');
      // Update agent state
      setAgent({ ...agent, has_passcode: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set passcode');
    } finally {
      setPasscodeVerifying(false);
    }
  };

  const handlePasscodeVerify = async () => {
    if (passcode.length !== 6) {
      toast.error('Please enter your 6-digit passcode');
      return;
    }

    setPasscodeVerifying(true);
    try {
      await axios.post(
        `${API}/passcode/verify`,
        { passcode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Passcode verified!');
      setShowPasscodeVerify(false);
      setIsEditing(true);
      setPasscode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid passcode');
    } finally {
      setPasscodeVerifying(false);
    }
  };

  const handlePasscodeChange = async () => {
    if (newPasscode.length !== 6) {
      toast.error('New passcode must be exactly 6 digits');
      return;
    }
    if (newPasscode !== confirmPasscode) {
      toast.error('Passcodes do not match');
      return;
    }

    setPasscodeVerifying(true);
    try {
      await axios.put(
        `${API}/passcode/change`,
        { current_passcode: currentPasscode, new_passcode: newPasscode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Passcode changed successfully!');
      setShowPasscodeChange(false);
      setCurrentPasscode('');
      setNewPasscode('');
      setConfirmPasscode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change passcode');
    } finally {
      setPasscodeVerifying(false);
    }
  };

  const handlePasscodeResetWithPassword = async () => {
    if (newPasscode.length !== 6) {
      toast.error('New passcode must be exactly 6 digits');
      return;
    }
    if (newPasscode !== confirmPasscode) {
      toast.error('Passcodes do not match');
      return;
    }
    if (!passcodeResetPassword) {
      toast.error('Please enter your account password');
      return;
    }

    setPasscodeVerifying(true);
    try {
      await axios.post(
        `${API}/passcode/reset`,
        { password: passcodeResetPassword, new_passcode: newPasscode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Passcode reset successfully!');
      setShowPasscodeChange(false);
      setPasscodeResetPassword('');
      setNewPasscode('');
      setConfirmPasscode('');
      setUsePasswordForPasscode(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset passcode');
    } finally {
      setPasscodeVerifying(false);
    }
  };

  const handleRemovePasscode = async () => {
    if (!window.confirm('Are you sure you want to remove passcode protection? Your profile will be less secure.')) {
      return;
    }

    try {
      await axios.delete(
        `${API}/passcode/remove`,
        { 
          data: { passcode: currentPasscode },
          headers: { Authorization: `Bearer ${token}` } 
        }
      );
      toast.success('Passcode removed successfully');
      setShowPasscodeChange(false);
      setCurrentPasscode('');
      setAgent({ ...agent, has_passcode: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove passcode');
    }
  };

  const handlePasscodeReset = async () => {
    if (newPasscode.length !== 6) {
      toast.error('New passcode must be exactly 6 digits');
      return;
    }
    if (newPasscode !== confirmPasscode) {
      toast.error('Passcodes do not match');
      return;
    }

    setPasscodeVerifying(true);
    try {
      await axios.post(
        `${API}/passcode/reset`,
        { password: currentPassword, new_passcode: newPasscode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Passcode reset successfully!');
      setShowPasscodeReset(false);
      setCurrentPassword('');
      setNewPasscode('');
      setConfirmPasscode('');
      setAgent({ ...agent, has_passcode: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset passcode');
    } finally {
      setPasscodeVerifying(false);
    }
  };

  const handlePasswordChange = async () => {
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setPasscodeVerifying(true);
    try {
      const payload = {
        new_password: newPassword
      };
      
      // Add either passcode or current password based on what's set
      if (agent?.has_passcode) {
        payload.passcode = passcode;
      } else {
        payload.current_password = currentPassword;
      }

      await axios.put(
        `${API}/password/change`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password changed successfully!');
      setShowPasswordChange(false);
      setPasscode('');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasscodeVerifying(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-2" style={{fontFamily: '"Playfair Display", serif'}}>Settings</h1>
          <p className="text-stone-600">Manage your account information</p>
        </div>

        <Card className="bg-white p-8 rounded-2xl card-shadow" data-testid="settings-card">
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              {/* Full Name */}
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block flex items-center gap-2">
                  <User size={16} strokeWidth={1.5} />
                  Full Name
                </Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={`h-14 border-2 rounded-lg text-lg px-4 transition-colors ${
                    !isEditing 
                      ? 'bg-stone-50 border-stone-200 opacity-70 cursor-not-allowed' 
                      : 'bg-white border-stone-300 hover:border-[#1A4D2E] focus:border-[#1A4D2E] focus:ring-2 focus:ring-[#1A4D2E]/20'
                  }`}
                  required={isEditing}
                />
              </div>
              
              {/* Email (always read-only) */}
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block flex items-center gap-2">
                  <Mail size={16} strokeWidth={1.5} />
                  Email
                </Label>
                <Input
                  type="email"
                  value={agent?.email || ''}
                  disabled
                  className="h-14 bg-stone-50 border-2 border-stone-200 rounded-lg text-lg px-4 opacity-70"
                />
                <p className="text-xs text-stone-500 mt-1">Email cannot be changed</p>
              </div>
              
              {/* Phone */}
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block flex items-center gap-2">
                  <Phone size={16} strokeWidth={1.5} />
                  Phone
                </Label>
                {isEditing ? (
                  <PhoneInput
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="h-14 border-2 rounded-lg text-lg px-4 transition-colors bg-white border-stone-300 hover:border-[#1A4D2E] focus:border-[#1A4D2E] focus:ring-2 focus:ring-[#1A4D2E]/20"
                  />
                ) : (
                  <Input
                    type="tel"
                    name="phone"
                    value={formatPhoneDisplay(formData.phone)}
                    disabled
                    placeholder="Not provided"
                    className="h-14 border-2 rounded-lg text-lg px-4 transition-colors bg-stone-50 border-stone-200 opacity-70 cursor-not-allowed"
                  />
                )}
              </div>

              {/* City & State */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block flex items-center gap-2">
                    <Building2 size={16} strokeWidth={1.5} />
                    City
                  </Label>
                  <Input
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    disabled={!isEditing}
                    placeholder="Enter city"
                    className={`h-14 border-2 rounded-lg text-lg px-4 transition-colors ${isEditing ? 'bg-white border-stone-300 hover:border-[#1A4D2E] focus:border-[#1A4D2E] focus:ring-2 focus:ring-[#1A4D2E]/20' : 'bg-stone-50 border-stone-200 opacity-70 cursor-not-allowed'}`}
                    data-testid="settings-city-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block flex items-center gap-2">
                    State
                  </Label>
                  <Select
                    value={formData.state}
                    onValueChange={(val) => setFormData(prev => ({ ...prev, state: val }))}
                    disabled={!isEditing}
                  >
                    <SelectTrigger
                      className={`h-14 w-full border-2 rounded-lg text-lg px-4 transition-colors ${isEditing ? 'bg-white border-stone-300 hover:border-[#1A4D2E] focus:border-[#1A4D2E] focus:ring-2 focus:ring-[#1A4D2E]/20' : 'bg-stone-50 border-stone-200 opacity-70 cursor-not-allowed'}`}
                      data-testid="settings-state-select"
                    >
                      <SelectValue placeholder="Select State" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Timezone */}
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block flex items-center gap-2">
                  <Clock size={16} strokeWidth={1.5} />
                  Timezone
                </Label>
                {isEditing ? (
                  <Select
                    value={formData.timezone}
                    onValueChange={(val) => setFormData(prev => ({ ...prev, timezone: val }))}
                  >
                    <SelectTrigger className="h-14 w-full bg-white border-2 border-stone-300 rounded-lg text-lg px-4 hover:border-[#1A4D2E] focus:border-[#1A4D2E] focus:ring-2 focus:ring-[#1A4D2E]/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {US_TIMEZONES.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type="text"
                    value={US_TIMEZONES.find(tz => tz.value === formData.timezone)?.label || 'Eastern Time - New York (ET)'}
                    disabled
                    className="h-14 bg-stone-50 border-2 border-stone-200 rounded-lg text-lg px-4 opacity-70 cursor-not-allowed"
                  />
                )}
              </div>

              {/* Email Signature */}
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block flex items-center gap-2">
                  <Mail size={16} strokeWidth={1.5} />
                  Email Signature <span className="text-xs normal-case text-stone-400">(Optional)</span>
                </Label>
                <Textarea
                  name="email_signature"
                  value={formData.email_signature}
                  onChange={handleChange}
                  disabled={!isEditing}
                  placeholder="Add a custom signature for your thank you emails (e.g., certifications, licenses, website)"
                  rows={4}
                  className={`border-2 rounded-lg text-base px-4 py-3 transition-colors resize-none ${
                    !isEditing 
                      ? 'bg-stone-50 border-stone-200 opacity-70 cursor-not-allowed' 
                      : 'bg-white border-stone-300 hover:border-[#1A4D2E] focus:border-[#1A4D2E] focus:ring-2 focus:ring-[#1A4D2E]/20'
                  }`}
                />
                <p className="text-xs text-stone-500 mt-2">
                  This signature will appear at the bottom of your thank you emails, after your name and contact info.
                </p>
              </div>

              {/* Agent-specific - Managing Broker Display */}
              {!isBroker && agent?.broker_id && agent?.broker_name && (
                <div>
                  <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block flex items-center gap-2">
                    <Building2 size={16} strokeWidth={1.5} />
                    Managing Broker
                  </Label>
                  <Input
                    type="text"
                    value={agent.broker_name}
                    disabled
                    className="h-14 bg-stone-50 border-2 border-stone-200 rounded-lg text-lg px-4 opacity-70 cursor-not-allowed"
                  />
                  <p className="text-xs text-stone-500 mt-1">Your managing broker</p>
                </div>
              )}

              {/* Professional Information - Available to all agents */}
              <>
                <div className="pt-4 border-t border-stone-200">
                  <h3 className="text-lg font-bold text-[#1A4D2E] mb-4">Professional Information</h3>
                </div>

                {/* License ID */}
                <div>
                  <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block flex items-center gap-2">
                    <IdCard size={16} strokeWidth={1.5} />
                    License ID
                  </Label>
                  <Input
                    type="text"
                    name="license_id"
                    value={formData.license_id}
                    onChange={handleChange}
                    disabled={!isEditing}
                    placeholder=""
                    className={`h-14 border-2 rounded-lg text-lg px-4 transition-colors ${
                      !isEditing 
                        ? 'bg-stone-50 border-stone-200 opacity-70 cursor-not-allowed' 
                        : 'bg-white border-stone-300 hover:border-[#1A4D2E] focus:border-[#1A4D2E] focus:ring-2 focus:ring-[#1A4D2E]/20'
                    }`}
                  />
                </div>

                {/* Office Address */}
                <div>
                  <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block flex items-center gap-2">
                    <Building2 size={16} strokeWidth={1.5} />
                    Office Address
                  </Label>
                  <Input
                    type="text"
                    name="office_address"
                    value={formData.office_address}
                    onChange={handleChange}
                    disabled={!isEditing}
                    placeholder=""
                    className={`h-14 border-2 rounded-lg text-lg px-4 transition-colors ${
                      !isEditing 
                        ? 'bg-stone-50 border-stone-200 opacity-70 cursor-not-allowed' 
                        : 'bg-white border-stone-300 hover:border-[#1A4D2E] focus:border-[#1A4D2E] focus:ring-2 focus:ring-[#1A4D2E]/20'
                    }`}
                  />
                </div>

                {/* Office Phone */}
                <div>
                  <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block flex items-center gap-2">
                    <Phone size={16} strokeWidth={1.5} />
                    Office Phone
                  </Label>
                  {isEditing ? (
                    <PhoneInput
                      name="office_phone"
                      value={formData.office_phone}
                      onChange={handleChange}
                      className="h-14 border-2 rounded-lg text-lg px-4 transition-colors bg-white border-stone-300 hover:border-[#1A4D2E] focus:border-[#1A4D2E] focus:ring-2 focus:ring-[#1A4D2E]/20"
                    />
                  ) : (
                    <Input
                      type="tel"
                      name="office_phone"
                      value={formatPhoneDisplay(formData.office_phone)}
                      disabled
                      placeholder=""
                      className="h-14 border-2 rounded-lg text-lg px-4 transition-colors bg-stone-50 border-stone-200 opacity-70 cursor-not-allowed"
                    />
                  )}
                </div>

                {/* Fax */}
                <div>
                  <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block flex items-center gap-2">
                    <Printer size={16} strokeWidth={1.5} />
                    Fax
                  </Label>
                  {isEditing ? (
                    <PhoneInput
                      name="fax"
                      value={formData.fax}
                      onChange={handleChange}
                      className="h-14 border-2 rounded-lg text-lg px-4 transition-colors bg-white border-stone-300 hover:border-[#1A4D2E] focus:border-[#1A4D2E] focus:ring-2 focus:ring-[#1A4D2E]/20"
                    />
                  ) : (
                    <Input
                      type="tel"
                      name="fax"
                      value={formatPhoneDisplay(formData.fax)}
                      disabled
                      placeholder="Not provided"
                      className="h-14 border-2 rounded-lg text-lg px-4 transition-colors bg-stone-50 border-stone-200 opacity-70 cursor-not-allowed"
                    />
                  )}
                </div>
              </>
            </div>
            
            {/* Action Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
              {!isEditing ? (
                <Button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    handleEditClick();
                  }}
                  className="w-full sm:w-auto bg-[#1A4D2E] text-white rounded-full px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg hover:bg-[#143D24] flex items-center justify-center gap-2"
                >
                  <Lock size={20} />
                  Edit Profile
                </Button>
              ) : (
                <>
                  <Button
                    type="submit"
                    disabled={isSaving}
                    className="w-full sm:w-auto bg-[#1A4D2E] text-white rounded-full px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg hover:bg-[#143D24] flex items-center justify-center gap-2"
                  >
                    <Save size={20} />
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      handleCancel();
                    }}
                    disabled={isSaving}
                    variant="outline"
                    className="w-full sm:w-auto border-2 border-stone-300 rounded-full px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg"
                  >
                    Cancel
                  </Button>
                </>
              )}
              
              {/* Create Passcode Button - Show only if user doesn't have a passcode */}
              {!agent?.has_passcode && !isEditing && (
                <Button
                  type="button"
                  onClick={() => setShowPasscodeSetup(true)}
                  variant="outline"
                  className="w-full sm:w-auto border-2 border-[#1A4D2E] text-[#1A4D2E] rounded-full px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg flex items-center justify-center gap-2 hover:bg-[#1A4D2E] hover:text-white"
                >
                  <Lock size={20} />
                  Create Passcode
                </Button>
              )}
              
              {/* Change Passcode Button */}
              {agent?.has_passcode && !isEditing && (
                <Button
                  type="button"
                  onClick={() => setShowPasscodeChange(true)}
                  variant="outline"
                  className="w-full sm:w-auto border-2 border-stone-300 rounded-full px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg flex items-center justify-center gap-2"
                >
                  <Key size={20} />
                  Change Passcode
                </Button>
              )}
              
              {/* Change Password Button */}
              {!isEditing && (
                <Button
                  type="button"
                  onClick={() => setShowPasswordChange(true)}
                  variant="outline"
                  className="w-full sm:w-auto border-2 border-stone-300 rounded-full px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg flex items-center justify-center gap-2"
                >
                  <Lock size={20} />
                  Change Password
                </Button>
              )}
            </div>
          </form>
        </Card>
      </div>

      {/* Passcode Setup Dialog */}
      <Dialog open={showPasscodeSetup} onOpenChange={setShowPasscodeSetup}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1A4D2E] flex items-center gap-2">
              <Lock size={24} />
              Set Up Security Passcode
            </DialogTitle>
            <DialogDescription>
              Create a 6 digit passcode to protect your profile. You'll need this passcode to edit your information.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Enter New Passcode (6 digits)</Label>
              <PasscodeInput
                length={6}
                value={newPasscode}
                onChange={setNewPasscode}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Confirm Passcode</Label>
              <PasscodeInput
                length={6}
                value={confirmPasscode}
                onChange={setConfirmPasscode}
                masked
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-2">
              <AlertCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-800">
                Make sure to remember this passcode. You'll need it every time you want to edit your profile.
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowPasscodeSetup(false);
                setNewPasscode('');
                setConfirmPasscode('');
              }}
              className="flex-1"
              disabled={passcodeVerifying}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePasscodeSetup}
              disabled={passcodeVerifying || newPasscode.length !== 6 || confirmPasscode.length !== 6}
              className="flex-1 bg-[#1A4D2E] hover:bg-[#15381F]"
            >
              {passcodeVerifying ? 'Setting Up...' : 'Set Passcode'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Passcode Verify Dialog */}
      <Dialog open={showPasscodeVerify} onOpenChange={setShowPasscodeVerify}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1A4D2E] flex items-center gap-2">
              <Lock size={24} />
              Enter Your Passcode
            </DialogTitle>
            <DialogDescription>
              Enter your security passcode to edit your profile.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Security Passcode</Label>
              <PasscodeInput
                length={6}
                value={passcode}
                onChange={setPasscode}
                autoFocus
                masked
              />
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowPasscodeVerify(false);
                setPasscode('');
              }}
              className="flex-1"
              disabled={passcodeVerifying}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePasscodeVerify}
              disabled={passcodeVerifying || passcode.length !== 6}
              className="flex-1 bg-[#1A4D2E] hover:bg-[#15381F]"
            >
              {passcodeVerifying ? 'Verifying...' : 'Verify'}
            </Button>
          </div>
          
          <div className="text-center mt-2">
            <button
              type="button"
              onClick={() => {
                setShowPasscodeVerify(false);
                setPasscode('');
                setShowPasscodeReset(true);
              }}
              className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              Forgot Passcode? Reset with Password
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Passcode Dialog */}
      <Dialog open={showPasscodeChange} onOpenChange={(open) => {
        setShowPasscodeChange(open);
        if (!open) { setCurrentPasscode(''); setNewPasscode(''); setConfirmPasscode(''); setUsePasswordForPasscode(false); setPasscodeResetPassword(''); }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1A4D2E] flex items-center gap-2">
              <Key size={24} />
              Change Passcode
            </DialogTitle>
            <DialogDescription>
              {usePasswordForPasscode
                ? 'Enter your account password to reset your passcode.'
                : 'Update your security passcode or remove it completely.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {usePasswordForPasscode ? (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Account Password</Label>
                <Input
                  type="password"
                  value={passcodeResetPassword}
                  onChange={(e) => setPasscodeResetPassword(e.target.value)}
                  placeholder="Enter your account password"
                  autoFocus
                  data-testid="passcode-reset-password"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Current Passcode</Label>
                <PasscodeInput
                  length={6}
                  value={currentPasscode}
                  onChange={setCurrentPasscode}
                  autoFocus
                  masked
                />
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-medium">New Passcode (6 digits)</Label>
              <PasscodeInput
                length={6}
                value={newPasscode}
                onChange={setNewPasscode}
                masked
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Confirm New Passcode</Label>
              <PasscodeInput
                length={6}
                value={confirmPasscode}
                onChange={setConfirmPasscode}
                masked
              />
            </div>

            {/* Toggle between passcode and password */}
            <button
              type="button"
              onClick={() => {
                setUsePasswordForPasscode(!usePasswordForPasscode);
                setCurrentPasscode('');
                setPasscodeResetPassword('');
              }}
              className="text-sm text-[#1A4D2E] hover:underline font-medium"
              data-testid="toggle-passcode-password"
            >
              {usePasswordForPasscode ? 'Use Current Passcode instead' : 'Forgot Passcode? Use Password'}
            </button>
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowPasscodeChange(false);
                  setCurrentPasscode('');
                  setNewPasscode('');
                  setConfirmPasscode('');
                  setUsePasswordForPasscode(false);
                  setPasscodeResetPassword('');
                }}
                className="flex-1"
                disabled={passcodeVerifying}
              >
                Cancel
              </Button>
              <Button
                onClick={usePasswordForPasscode ? handlePasscodeResetWithPassword : handlePasscodeChange}
                disabled={passcodeVerifying || (usePasswordForPasscode ? !passcodeResetPassword : currentPasscode.length !== 6) || newPasscode.length !== 6 || confirmPasscode.length !== 6}
                className="flex-1 bg-[#1A4D2E] hover:bg-[#15381F]"
              >
                {passcodeVerifying ? 'Updating...' : 'Update Passcode'}
              </Button>
            </div>
            <Button
              variant="destructive"
              onClick={handleRemovePasscode}
              disabled={passcodeVerifying || currentPasscode.length < 4}
              className="w-full"
            >
              Remove Passcode Protection
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Passcode with Password Dialog */}
      <Dialog open={showPasscodeReset} onOpenChange={setShowPasscodeReset}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1A4D2E] flex items-center gap-2">
              <Key size={24} />
              Reset Passcode
            </DialogTitle>
            <DialogDescription>
              Forgot your passcode? Reset it using your account password.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Account Password</Label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your account password"
                className="h-12"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">New Passcode (6 digits)</Label>
              <PasscodeInput
                length={6}
                value={newPasscode}
                onChange={setNewPasscode}
                masked
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Confirm New Passcode</Label>
              <PasscodeInput
                length={6}
                value={confirmPasscode}
                onChange={setConfirmPasscode}
                masked
              />
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex gap-2">
              <AlertCircle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-800">
                After reset, remember your new passcode. You'll need it to edit your profile.
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowPasscodeReset(false);
                setCurrentPassword('');
                setNewPasscode('');
                setConfirmPasscode('');
              }}
              className="flex-1"
              disabled={passcodeVerifying}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePasscodeReset}
              disabled={passcodeVerifying || !currentPassword || newPasscode.length !== 6 || confirmPasscode.length !== 6}
              className="flex-1 bg-[#1A4D2E] hover:bg-[#15381F]"
            >
              {passcodeVerifying ? 'Resetting...' : 'Reset Passcode'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordChange} onOpenChange={setShowPasswordChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1A4D2E] flex items-center gap-2">
              <Lock size={24} />
              Change Password
            </DialogTitle>
            <DialogDescription>
              {agent?.has_passcode 
                ? 'Verify your passcode to change your account password.' 
                : 'Enter your current password to set a new one.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {agent?.has_passcode ? (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Security Passcode</Label>
                <PasscodeInput
                  length={6}
                  value={passcode}
                  onChange={setPasscode}
                  autoFocus
                  masked
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Current Password</Label>
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  className="h-12"
                  autoFocus
                />
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-medium">New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="h-12"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Confirm New Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                className="h-12"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-2">
              <AlertCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-800">
                {agent?.has_passcode 
                  ? 'Using your passcode for extra security.' 
                  : 'Your new password must be at least 8 characters long.'}
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowPasswordChange(false);
                setPasscode('');
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
              }}
              className="flex-1"
              disabled={passcodeVerifying}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePasswordChange}
              disabled={passcodeVerifying || 
                (agent?.has_passcode ? passcode.length !== 6 : !currentPassword) || 
                newPassword.length < 8 || confirmPassword.length < 8}
              className="flex-1 bg-[#1A4D2E] hover:bg-[#15381F]"
            >
              {passcodeVerifying ? 'Changing...' : 'Change Password'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Settings;
